import React, { JSX } from 'react';
import Image from 'next/image';
import styled from 'styled-components';
import { utils } from '@makeship/core';
import { getProductFromContext, getStretchGoalsStateFromContext } from '../../context/product';
import { ProductStage } from '../../types/common';
import { getStage, parseMetafields } from '../../utils/product';
import ImageGallery from '../ImageGallery';
import Product360Viewer from '../pages/products/Product360Viewer';
import MiniCarousel from '../MiniCarousel';

const ProductCarouselWrapper = styled.div`
  flex: 1 1;
  padding-right: 40px;
  position: relative;
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.largeDesktop}px) {
    padding-right: 20px;
  }
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    padding-right: 0;
  }
`;

const BrandOverlay = styled.div`
  position: absolute;
  bottom: 16px;
  right: 4px;
  margin-top: 70%;
  width: 90px;

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    margin-top: 83%;
  }

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    bottom: 0;
    right: 0;
  }
`;

const BrandOverlayDWOB = styled.div`
  display: block;
  position: absolute;
  right: 0;
  bottom: 0;
  background: rgba(239, 239, 239, 0.8);
  border-radius: 8px;
  padding: 8px;
`;

const ImageGalleryWrapper = styled.div`
  display: block;
  width: 100%;
  position: -webkit-sticky;
  position: sticky;
  top: 112px;

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    display: none;
  }
`;

const ImageCarouselWrapper = styled.div`
  display: none;
  margin-top: 16px;
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    display: block;
  }
`;

const StyledImage = styled(Image)`
  width: 100%;
  object-fit: contain;

  border-radius: 0px 8px 8px 8px;
  background-color: ${({ theme }) => utils.hexToRGBA(theme.colors.cloud, theme.alpha.light)};
`;

const StyledImageBorder = styled.div`
  display: flex;
  border-radius: 0px 8px 8px 8px;
  border: 1px solid ${({ theme }) => utils.hexToRGBA(theme.colors.midnight, theme.alpha.extraLight)};
`;

export const renderBrandOverlay = (metafields: Metafields, stretchGoalsState: StretchGoalsState) => {
  const { shouldEnableStretchGoals, goalsAchieved } = stretchGoalsState;
  if (shouldEnableStretchGoals) {
    return (
      <BrandOverlay>
        <Image
          src={
            goalsAchieved
              ? '/assets/stretch-goals/free-gift-webtag.png'
              : '/assets/stretch-goals/unlock-gift-webtag.png'
          }
          width={80}
          height={80}
          unoptimized
          alt="Unlock free gift badge"
        />
      </BrandOverlay>
    );
  }
  if (metafields.promotion === 'dwob')
    return (
      <BrandOverlayDWOB>
        <Image
          src="/assets/dwob-logo.png"
          width={110}
          height={45}
          layout="fixed"
          unoptimized
          alt="Doctors without Borders overlay"
        />
      </BrandOverlayDWOB>
    );
  if (metafields.brand && metafields.promotion !== 'dwob') {
    return (
      <BrandOverlay>
        <Image src={metafields.brand} width={160} height={160} unoptimized alt="Product Brand overlay" />
      </BrandOverlay>
    );
  }
};

type ProductPageCarouselProps = {
  productImages: Shopify.ImageEdge[];
  product360Images: Shopify.ImageEdge[] | null;
  currentHas360View: boolean;
  isPreview?: boolean;
};

const ProductPageCarousel: React.FC<ProductPageCarouselProps> = ({
  productImages,
  product360Images,
  currentHas360View,
  isPreview = false,
}: ProductPageCarouselProps) => {
  const product = getProductFromContext();
  const stretchGoalsState = getStretchGoalsStateFromContext();

  const stage = getStage(product.tags);
  const metafields = parseMetafields(product.metafields);
  const renderCarouselCards = (
    productImages: Shopify.ImageEdge[],
    product360Images: Shopify.ImageEdge[] | null,
  ): JSX.Element[] => {
    let elements: JSX.Element[] = productImages.map((edge, index) => (
      <StyledImageBorder>
        <StyledImage
          key={edge.node.transformedSrc}
          data-testid="mobile-image"
          src={edge.node.transformedSrc}
          alt={edge.node.altText || 'product image'}
          width={1000}
          height={1000}
          quality={90}
          priority={index === 0}
          unoptimized
        />
      </StyledImageBorder>
    ));

    if (product360Images && product360Images.length > 0) {
      elements = [
        <div data-testid="product-360-viewer-mobile">
          <Product360Viewer imageArray={product360Images?.map((edge): Shopify.Image => edge.node)} />
        </div>,
      ].concat(elements);
    }

    return elements;
  };

  return (
    <ProductCarouselWrapper>
      <ImageGalleryWrapper>
        <ImageGallery
          images={productImages.map((edge): Shopify.Image => edge.node)}
          images360={product360Images?.map((edge): Shopify.Image => edge.node) || []}
          has360View={currentHas360View}
          product={product}
          isComingSoon={stage === ProductStage.ComingSoon}
          stretchGoalsState={stretchGoalsState}
          isPreview={isPreview}
        />
      </ImageGalleryWrapper>
      <ImageCarouselWrapper>
        <MiniCarousel product={product} isComingSoon={stage === ProductStage.ComingSoon} isPreview={isPreview}>
          {productImages.length > 0 || (product360Images && product360Images.length > 0)
            ? renderCarouselCards(productImages, product360Images)
            : [
                <StyledImageBorder>
                  <StyledImage
                    key="/assets/default_product_asset.gif"
                    src="/assets/default_product_asset.gif"
                    alt="product image"
                    width={1000}
                    height={1000}
                    quality={90}
                  />
                </StyledImageBorder>,
              ]}
        </MiniCarousel>
        {renderBrandOverlay(metafields, stretchGoalsState)}
      </ImageCarouselWrapper>
    </ProductCarouselWrapper>
  );
};

export default ProductPageCarousel;
